import { Howl } from "howler";

const SOUND_URLS = {
  matchFound:
    "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/sounds/match-found-notification-sound-3.mp3",
  acceptTick:
    "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/sounds/accept-tick.mp3",
  matchPageLanded:
    "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/sounds/match-page-landed.mp3",
};

const MATCH_PAGE_LANDED_ARMED_MATCH_ID_KEY =
  "ouro.matchPageLandedArmedMatchId.v1";
const MATCH_PAGE_LANDED_MUTE_KEY = "ouro.matchPageLandedSoundMuted.v1";
const MATCH_FOUND_MUTE_KEY = "ouro.matchFoundSoundMuted.v1";
const MATCH_ACCEPT_MUTE_KEY = "ouro.matchAcceptSoundMuted.v1";
const MATCH_PAGE_LANDED_VOLUME_MULTIPLIER = 0.85;

/**
 * Module-level singleton sound bank.
 * Howl instances are created once per session so `preload: true` fetches the
 * files immediately on lobby mount rather than on first play event.
 */
const bank = {
  matchFound: null,
  acceptTick: null,
  matchPageLanded: null,
  primed: false,
  preloadPromise: null,
};

function makeHowl(url, volume = 0.7) {
  return new Howl({
    src: [url],
    volume,
    // Use Web Audio mode for more reliable preloading of short UI sounds.
    html5: false,
    preload: true,
    onloaderror: () => {
      // Non-fatal: CORS or network issues must never break the UI.
    },
  });
}

function preloadHowl(sound) {
  if (!sound) return Promise.resolve();
  const state = sound.state();
  if (state === "loaded") return Promise.resolve();

  return new Promise((resolve) => {
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };

    sound.once("load", finish);
    sound.once("loaderror", finish);

    // Only trigger a new load when unloaded. If already loading, just wait.
    if (state === "loading") return;

    try {
      sound.load();
    } catch (_) {
      finish();
    }
  });
}

function playSound(sound) {
  if (!sound) return;
  const state = sound.state();
  if (state === "loaded") {
    try {
      // Restart from the beginning only when explicitly re-triggered.
      // Callers that need once-per-match semantics must gate externally.
      if (sound.playing()) {
        sound.stop();
      }
      sound.play();
    } catch (_) {
      // Non-fatal.
    }
    return;
  }

  // If not ready yet, play as soon as the resource finishes loading.
  // Use a one-shot flag so stacked once("load") handlers from remounts
  // cannot schedule multiple delayed plays.
  if (sound.__ouroPlayOnLoadPending) return;
  sound.__ouroPlayOnLoadPending = true;
  sound.once("load", () => {
    sound.__ouroPlayOnLoadPending = false;
    try {
      sound.play();
    } catch (_) {
      // Non-fatal.
    }
  });

  if (state === "loading") return;

  try {
    sound.load();
  } catch (_) {
    sound.__ouroPlayOnLoadPending = false;
  }
}

/**
 * Pre-download all three sounds. Safe to call multiple times — will only
 * create the Howl instances once. Call this from Lobby mount so files are
 * ready well before any dialog or redirect fires.
 */
export function primeMatchNotificationSounds() {
  if (typeof window === "undefined") return;
  if (!bank.primed) {
    bank.primed = true;

    bank.matchFound = makeHowl(SOUND_URLS.matchFound);
    bank.acceptTick = makeHowl(SOUND_URLS.acceptTick);
    bank.matchPageLanded = makeHowl(
      SOUND_URLS.matchPageLanded,
      0.7 * MATCH_PAGE_LANDED_VOLUME_MULTIPLIER,
    );
  }

  if (!bank.preloadPromise) {
    bank.preloadPromise = Promise.all([
      preloadHowl(bank.matchFound),
      preloadHowl(bank.acceptTick),
      preloadHowl(bank.matchPageLanded),
    ]).then(() => undefined);
  }
}

export function waitForMatchNotificationSoundsReady() {
  safePrime();
  return bank.preloadPromise || Promise.resolve();
}

function safePrime() {
  // Lazily ensure sounds exist even if primeMatchNotificationSounds was not
  // called (e.g. user lands directly on /match/:id without visiting Lobby).
  if (!bank.primed) primeMatchNotificationSounds();
}

export function playMatchFoundSound() {
  try {
    if (isMatchFoundSoundMuted()) return;
    safePrime();
    playSound(bank.matchFound);
  } catch (_) {
    // Non-fatal.
  }
}

/**
 * Play the match-found cue at most once per provisional matchId for the
 * lifetime of this tab. Handshake resyncs and lobby remounts re-dispatch the
 * same notification; without this guard the cue restarts on every navigation.
 */
const playedMatchFoundSoundIds = new Set();

export function playMatchFoundSoundOnce(matchId) {
  const id =
    matchId != null && matchId !== "" ? String(matchId) : null;
  if (!id || playedMatchFoundSoundIds.has(id)) {
    return false;
  }
  playedMatchFoundSoundIds.add(id);
  playMatchFoundSound();
  return true;
}

export function clearMatchFoundSoundOnce(matchId) {
  const id =
    matchId != null && matchId !== "" ? String(matchId) : null;
  if (!id) return;
  playedMatchFoundSoundIds.delete(id);
}

export function playMatchAcceptSound() {
  try {
    if (isMatchAcceptSoundMuted()) return;
    safePrime();
    playSound(bank.acceptTick);
  } catch (_) {
    // Non-fatal.
  }
}

function readMatchPageLandedMuteFromStorage() {
  return readMuteFlag(MATCH_PAGE_LANDED_MUTE_KEY);
}

function writeMuteFlag(storageKey, muted) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (muted) {
      window.localStorage.setItem(storageKey, "1");
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch (_) {
    // Non-fatal.
  }
}

function readMuteFlag(storageKey) {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(storageKey) === "1";
  } catch (_) {
    return false;
  }
}

export function setMatchPageLandedSoundMuted(muted) {
  writeMuteFlag(MATCH_PAGE_LANDED_MUTE_KEY, muted);
}

export function setMatchFoundSoundMuted(muted) {
  writeMuteFlag(MATCH_FOUND_MUTE_KEY, muted);
}

export function setMatchAcceptSoundMuted(muted) {
  writeMuteFlag(MATCH_ACCEPT_MUTE_KEY, muted);
}

export function syncMatchPageLandedSoundMuteFromUser(user) {
  if (!user || typeof user !== "object") return;
  const prefs = user.soundPreferences;
  if (!prefs || typeof prefs !== "object" || Array.isArray(prefs)) return;
  if (Object.prototype.hasOwnProperty.call(prefs, "matchPageLandedMuted")) {
    setMatchPageLandedSoundMuted(prefs.matchPageLandedMuted === true);
  }
  if (Object.prototype.hasOwnProperty.call(prefs, "matchFoundMuted")) {
    setMatchFoundSoundMuted(prefs.matchFoundMuted === true);
  }
  if (Object.prototype.hasOwnProperty.call(prefs, "matchAcceptMuted")) {
    setMatchAcceptSoundMuted(prefs.matchAcceptMuted === true);
  }
}

export function isMatchPageLandedSoundMuted(user) {
  if (user?.soundPreferences?.matchPageLandedMuted === true) return true;
  return readMatchPageLandedMuteFromStorage();
}

export function isMatchFoundSoundMuted(user) {
  if (user?.soundPreferences?.matchFoundMuted === true) return true;
  return readMuteFlag(MATCH_FOUND_MUTE_KEY);
}

export function isMatchAcceptSoundMuted(user) {
  if (user?.soundPreferences?.matchAcceptMuted === true) return true;
  return readMuteFlag(MATCH_ACCEPT_MUTE_KEY);
}

export function playMatchPageLandedSound(user) {
  try {
    if (isMatchPageLandedSoundMuted(user)) return;
    safePrime();
    playSound(bank.matchPageLanded);
  } catch (_) {
    // Non-fatal.
  }
}

let lastSmallerMatchOfferSoundKey = null;

/**
 * Play once when a smaller-match offer first appears. Ticks that repeat the
 * same 2v2/3v3/4v4 must not retrigger audio every 30s.
 */
export function playSmallerMatchOfferSoundOnce(offer) {
  const teamSize = Number(offer?.teamSize);
  const status = offer?.status || "offered";
  if (!Number.isInteger(teamSize) || status === "declined") {
    return false;
  }
  if (status === "opted_in") {
    return false;
  }

  const key = `${teamSize}`;
  if (lastSmallerMatchOfferSoundKey === key) {
    return false;
  }
  lastSmallerMatchOfferSoundKey = key;

  try {
    safePrime();
    playSound(bank.matchFound);
  } catch (_) {
    return false;
  }
  return true;
}

export function clearSmallerMatchOfferSound() {
  lastSmallerMatchOfferSoundKey = null;
}

function normalizeMatchId(matchId) {
  const normalized = String(matchId || "").trim();
  return normalized || null;
}

export function armMatchPageLandedSoundForMatch(matchId) {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  const normalizedMatchId = normalizeMatchId(matchId);
  if (!normalizedMatchId) return;

  try {
    window.sessionStorage.setItem(
      MATCH_PAGE_LANDED_ARMED_MATCH_ID_KEY,
      normalizedMatchId,
    );
  } catch (_) {
    // Non-fatal.
  }
}

export function consumeMatchPageLandedSoundForMatch(matchId) {
  if (typeof window === "undefined" || !window.sessionStorage) return false;
  const normalizedMatchId = normalizeMatchId(matchId);
  if (!normalizedMatchId) return false;

  try {
    const armedMatchId = window.sessionStorage.getItem(
      MATCH_PAGE_LANDED_ARMED_MATCH_ID_KEY,
    );
    if (armedMatchId !== normalizedMatchId) return false;

    window.sessionStorage.removeItem(MATCH_PAGE_LANDED_ARMED_MATCH_ID_KEY);
    return true;
  } catch (_) {
    return false;
  }
}
